import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TestTube, Sparkles, Search, Home as HomeIcon, Beaker, ShoppingCart } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const LabTests = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [sp] = useSearchParams();
    const [tests, setTests] = useState([]);
    const [packages, setPackages] = useState([]);
    const [cats, setCats] = useState([]);
    const [filterCat, setFilterCat] = useState(sp.get("category") || "");
    const [q, setQ] = useState(sp.get("q") || "");
    const [cart, setCart] = useState([]); // [{test_id|package_id, name, price}]
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/lab-tests", { params: { ...(filterCat ? { category: filterCat } : {}), ...(q ? { q } : {}), page_size: 60 } }).then((r) => { setTests(r.data.items || []); setCats(r.data.categories || []); }),
            api.get("/lab-packages", { params: { popular: true } }).then((r) => setPackages(r.data.items || [])),
        ]).finally(() => setLoading(false));
    }, [filterCat, q]);

    const toggleCart = (item, isPackage) => {
        const key = isPackage ? `pkg-${item.id}` : `test-${item.id}`;
        setCart((prev) => {
            const found = prev.find((x) => x.key === key);
            if (found) return prev.filter((x) => x.key !== key);
            return [...prev, { key, [isPackage ? "package_id" : "test_id"]: item.id, name: item.name, price: item.selling_price }];
        });
    };
    const inCart = (id, isPackage) => cart.some((x) => x.key === (isPackage ? `pkg-${id}` : `test-${id}`));
    const total = useMemo(() => cart.reduce((s, x) => s + Number(x.price || 0), 0), [cart]);

    const proceedToBook = () => {
        if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
        if (cart.length === 0) { toast.error("Pick at least one test"); return; }
        sessionStorage.setItem("lab-cart", JSON.stringify(cart));
        navigate("/lab-tests/book");
    };

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-6xl px-6 py-12 lg:px-12">
                <p className="label-uppercase text-zinc-500">Diagnostics</p>
                <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>Book a lab test</h1>
                <p className="mt-2 max-w-xl text-sm text-zinc-500">Free home sample collection, certified labs, online reports within hours.</p>

                {/* Search */}
                <div className="mt-6 relative max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
                    <input type="text" placeholder="Search tests…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full rounded-full border border-zinc-200 pl-9 pr-4 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-search" />
                </div>

                {/* Packages */}
                {packages.length > 0 && (
                    <section className="mt-10">
                        <div className="mb-4 flex items-end justify-between">
                            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Popular health packages</h2>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="lab-packages-grid">
                            {packages.map((p) => (
                                <div key={p.id} className="relative rounded-2xl border border-zinc-100 bg-gradient-to-br from-emerald-50/60 to-white p-5 dark:from-emerald-950/20 dark:to-zinc-900 dark:border-white/10" data-testid={`lab-package-${p.slug}`}>
                                    <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Save {inr(p.savings_amount)}</span>
                                    <Sparkles className="h-5 w-5 text-emerald-700" />
                                    <Link to={`/lab-packages/${p.slug}`} className="mt-3 block font-medium text-zinc-900 dark:text-zinc-50 hover:underline">{p.name}</Link>
                                    <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{p.description}</p>
                                    <p className="mt-3"><span className="text-xs line-through text-zinc-400">{inr(p.mrp)}</span> <span className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{inr(p.selling_price)}</span></p>
                                    <button onClick={() => toggleCart(p, true)} className={`mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${inCart(p.id, true) ? "bg-emerald-700 text-white" : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`lab-package-toggle-${p.slug}`}>
                                        {inCart(p.id, true) ? "✓ Added" : "+ Add to cart"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Tests */}
                <section className="mt-12">
                    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">Individual tests</h2>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-1.5" data-testid="lab-cat-chips">
                        <button onClick={() => setFilterCat("")} className={`chip ${!filterCat ? "chip-active" : ""}`} data-testid="lab-cat-all">All</button>
                        {cats.map((c) => (<button key={c} onClick={() => setFilterCat(c)} className={`chip ${filterCat === c ? "chip-active" : ""}`} data-testid={`lab-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}>{c}</button>))}
                    </div>
                    {loading ? <p className="text-sm text-zinc-500">Loading…</p> : tests.length === 0 ? (
                        <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">No tests match this filter.</p>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="lab-tests-grid">
                            {tests.map((t) => (
                                <div key={t.id} className="rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03] dark:border-white/10" data-testid={`lab-test-${t.slug}`}>
                                    <div className="flex items-start gap-3">
                                        {t.home_collection_available ? <HomeIcon className="h-4 w-4 shrink-0 text-emerald-600" /> : <Beaker className="h-4 w-4 shrink-0 text-zinc-500" />}
                                        <div className="min-w-0 flex-1">
                                            <Link to={`/lab-tests/${t.slug}`} className="block font-medium text-zinc-900 dark:text-zinc-50 hover:underline">{t.name}</Link>
                                            <p className="text-[11px] text-zinc-500">{t.category} • {t.reporting_time}</p>
                                            <div className="mt-2 flex items-baseline justify-between">
                                                <p>{t.mrp > t.selling_price && <span className="mr-1 text-xs line-through text-zinc-400">{inr(t.mrp)}</span>}<span className="text-base font-semibold text-zinc-900 dark:text-zinc-50">{inr(t.selling_price)}</span></p>
                                                <button onClick={() => toggleCart(t, false)} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${inCart(t.id, false) ? "bg-brand-700 text-white" : "border border-brand-200 text-brand-700 hover:bg-brand-50"}`} data-testid={`lab-test-toggle-${t.slug}`}>
                                                    {inCart(t.id, false) ? "✓" : "+ Add"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Sticky cart bar */}
                {cart.length > 0 && (
                    <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-full bg-zinc-900 px-5 py-3 text-white shadow-xl dark:bg-zinc-100 dark:text-zinc-900" data-testid="lab-cart-bar">
                        <button onClick={proceedToBook} className="flex items-center gap-3">
                            <ShoppingCart className="h-4 w-4" />
                            <span className="text-sm">{cart.length} item{cart.length === 1 ? "" : "s"} · <span className="font-semibold">{inr(total)}</span></span>
                            <span className="ml-2 rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white">Book →</span>
                        </button>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default LabTests;
